import React from 'react';
import { Col, Modal, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import Button from '../../../../../components/button';
import {
  Datepicker,
  Form as CustomForm,
  Input,
  SelectDropdownControlled,
} from '../../../../../components/form';
import InputLabel from '../../../../../components/form/InputLabel';
import { FormGroup } from '../../../../../components/form/FormGroup';
import { useToasts } from '../../../../../context/Toast';
import { useMutationErrorHandler } from '../../../../../hooks/useMutationErrorHandler';
import { toISODateOnly } from '../../../../../utils/dateUtils';
import { parseISO } from 'date-fns';
import {
  useCreateMyExperienceMutation,
  useUpdateMyExperienceMutation,
  useExperienceFormDataQuery,
} from '../../../__generated__/graphql';

export type ExperienceRecord = {
  id: string;
  name: string;
  title: string | null;
  companyName: string | null;
  companyUrl: string | null;
  companyIndustryId: string | null;
  employmentTypeId: string | null;
  location: string | null;
  startDate: string | null;
  endDate: string | null;
  description: string | null;
};

type Props = {
  show: boolean;
  onClose: () => void;
  onSave: () => void;
  experience: ExperienceRecord | null;
};

type FormData = {
  title: string;
  companyName: string;
  companyUrl: string;
  companyIndustryId: string | null;
  employmentTypeId: string | null;
  location: string;
  startDate: Date | null;
  endDate: Date | null;
  description: string;
};

const formSchema: yup.ObjectSchema<FormData> = yup.object({
  title: yup.string().required(),
  companyName: yup.string().defined(),
  companyUrl: yup.string().defined(),
  companyIndustryId: yup.string().nullable().defined(),
  employmentTypeId: yup.string().nullable().defined(),
  location: yup.string().defined(),
  startDate: yup.date().nullable().defined(),
  endDate: yup.date().nullable().defined(),
  description: yup.string().defined(),
});

export const ExperienceSaveModal: React.FC<React.PropsWithChildren<Props>> = ({
  show,
  onClose,
  onSave,
  experience,
}) => {
  const { t } = useTranslation();
  const { addSuccess } = useToasts();
  const errorHandler = useMutationErrorHandler();

  const { data: optionsData } = useExperienceFormDataQuery();

  const [createExperience, { loading: createLoading }] =
    useCreateMyExperienceMutation();
  const [updateExperience, { loading: updateLoading }] =
    useUpdateMyExperienceMutation();

  const employmentTypeOptions = (optionsData?.employmentTypes ?? []).map(
    (item) => ({
      value: item.Id,
      label: item.label || item.Name,
    }),
  );

  const industryOptions = (optionsData?.industries ?? []).map((item) => ({
    value: item.Id,
    label: item.label || item.Name,
  }));

  const defaultValues: FormData = {
    title: experience?.title ?? '',
    companyName: experience?.companyName ?? '',
    companyUrl: experience?.companyUrl ?? '',
    companyIndustryId: experience?.companyIndustryId ?? null,
    employmentTypeId: experience?.employmentTypeId ?? null,
    location: experience?.location ?? '',
    startDate: experience?.startDate ? parseISO(experience.startDate) : null,
    endDate: experience?.endDate ? parseISO(experience.endDate) : null,
    description: experience?.description ?? '',
  };

  const handleSubmit = async (formData: FormData) => {
    const input = {
      name: formData.title.slice(0, 80),
      title: formData.title,
      companyName: formData.companyName || null,
      companyUrl: formData.companyUrl || null,
      companyIndustryId: formData.companyIndustryId || null,
      employmentTypeId: formData.employmentTypeId || null,
      location: formData.location || null,
      startDate: formData.startDate ? toISODateOnly(formData.startDate) : null,
      endDate: formData.endDate ? toISODateOnly(formData.endDate) : null,
      description: formData.description || null,
    };

    if (experience) {
      await updateExperience({
        variables: { input: { id: experience.id, ...input } },
      })
        .then(() => {
          addSuccess(t('personalData.experience.updateSuccess'));
          onSave();
        })
        .catch(errorHandler);
    } else {
      await createExperience({
        variables: { input },
      })
        .then(() => {
          addSuccess(t('personalData.experience.createSuccess'));
          onSave();
        })
        .catch(errorHandler);
    }
  };

  return (
    <Modal enforceFocus={false} show={show} centered onHide={onClose} size="lg">
      <Modal.Header closeButton className="card-header">
        <h4 className="card-header-title">
          {experience
            ? t('personalData.experience.editTitle')
            : t('personalData.experience.createTitle')}
        </h4>
      </Modal.Header>
      <CustomForm
        onSubmit={handleSubmit}
        validationSchema={formSchema}
        defaultValues={defaultValues}>
        <Modal.Body>
          <Row>
            <Col md={6}>
              <Input
                name="title"
                label={t('personalData.experience.fields.title')}
                required
              />
            </Col>
            <Col md={6}>
              <Input
                name="companyName"
                label={t('personalData.experience.fields.companyName')}
              />
            </Col>
            <Col md={6}>
              <Input
                name="companyUrl"
                label={t('personalData.experience.fields.companyUrl')}
              />
            </Col>
            <Col md={6}>
              <FormGroup>
                <InputLabel
                  label={t('personalData.experience.fields.companyIndustry')}
                />
                <SelectDropdownControlled
                  name="companyIndustryId"
                  options={industryOptions}
                  placeholder=""
                  heightMode="large"
                  isClearable
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <InputLabel
                  label={t('personalData.experience.fields.employmentType')}
                />
                <SelectDropdownControlled
                  name="employmentTypeId"
                  options={employmentTypeOptions}
                  placeholder=""
                  heightMode="large"
                  isClearable
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <Input
                name="location"
                label={t('personalData.experience.fields.location')}
              />
            </Col>
            <Col md={6}>
              <Datepicker
                name="startDate"
                label={t('personalData.experience.fields.startDate')}
              />
            </Col>
            <Col md={6}>
              <Datepicker
                name="endDate"
                label={t('personalData.experience.fields.endDate')}
              />
            </Col>
            <Col md={12}>
              <Input
                name="description"
                label={t('personalData.experience.fields.description')}
                type="textarea"
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex">
            <Button
              label={t('form.general.cancelButton')}
              size="sm"
              className="mr-2 me-2"
              variant="outline-secondary"
              onClick={onClose}
            />
            <Button
              label={t('form.general.confirmButton')}
              size="sm"
              variant="primary"
              type="submit"
              isProcessing={createLoading || updateLoading}
            />
          </div>
        </Modal.Footer>
      </CustomForm>
    </Modal>
  );
};
